/**
 * Utility functions for making fetch API calls with standardized configuration
 * This ensures all API calls include Authorization header for JWT-based authentication
 * and handles token refresh
 */

import { API_CONFIG } from "@/infrastructure/config/api";
import { toast } from "@/shared/hooks/use-toast";

/**
 * Represents an API error with additional response metadata
 */
export interface ApiError extends Error {
  status?: number;
  code?: string;
  retryAfter?: number;
  data?: unknown;
  response?: { status: number; data: unknown };
}

/**
 * Normaliza uma `Response` não-2xx de um `fetch` cru (fora do `fetchApi`) num
 * `ApiError`, preservando `code`/`status`/`data` do envelope do backend. Isso
 * permite que `resolveApiErrorMessage` traduza por `code` em vez de vazar a
 * mensagem crua (frequentemente inglesa, ex.: validação do bodyparser do Adonis).
 *
 * Mesma lógica de parse do branch `!response.ok` de `fetchApi`, extraída para
 * reuso pelos serviços que precisam de `fetch` cru (uploads multipart, downloads).
 */
export async function throwFromResponse(response: Response): Promise<never> {
  let errorData: { code?: string; message?: string; error?: string } | null =
    null;
  try {
    const responseText = await response.text();
    errorData = responseText ? JSON.parse(responseText) : null;
  } catch {
    errorData = null;
  }

  const apiError = new Error(
    errorData?.message ||
      errorData?.error ||
      `Request failed with status ${response.status}`,
  ) as ApiError;
  apiError.status = response.status;
  apiError.code = errorData?.code;
  apiError.data = errorData;
  apiError.response = { status: response.status, data: errorData };
  throw apiError;
}

/**
 * Cria um `ApiError` com `code`/`status` sintéticos para guards client-side
 * (resposta malformada, stream quebrado, sessão ausente). O `message` continua
 * em inglês para debug/console, mas o `resolveApiErrorMessage` traduz pelo `code`.
 */
export function makeApiError(
  message: string,
  opts?: { code?: string; status?: number; data?: unknown },
): ApiError {
  const apiError = new Error(message) as ApiError;
  if (opts?.code) apiError.code = opts.code;
  if (opts?.status !== undefined) apiError.status = opts.status;
  apiError.data = opts?.data ?? (opts?.code ? { code: opts.code } : undefined);
  return apiError;
}

/**
 * Options for the fetchApi function
 */
export interface FetchApiOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
  parseJson?: boolean;
  _retry?: boolean;
  /**
   * Override base URL (e.g. API_CONFIG.API_ROOT_URL).
   * When set, `path` is appended raw (no `/api/` prefix).
   */
  baseUrl?: string;
}

let isRefreshing = false;

const isPublicPage = (): boolean => {
  const pathname = window.location.pathname;
  const isOAuthConsentPage = pathname.startsWith("/oauth/consent");
  return (
    pathname.startsWith("/login") ||
    pathname.startsWith("/auth") ||
    (pathname.startsWith("/oauth") && !isOAuthConsentPage) ||
    pathname.startsWith("/register") ||
    pathname.startsWith("/signup") ||
    pathname.startsWith("/recover-password") ||
    pathname.startsWith("/unsubscribe")
  );
};

interface QueueItem<T = unknown> {
  resolve: (value: T | PromiseLike<T>) => void;
  reject: (reason?: unknown) => void;
  request: () => Promise<T>;
}

let refreshQueue: QueueItem[] = [];

const processQueue = (error: unknown = null) => {
  refreshQueue.forEach(({ resolve, reject, request }) => {
    if (error) {
      reject(error);
    } else {
      resolve(request());
    }
  });
  refreshQueue = [];
};

async function refreshAuthToken(): Promise<boolean> {
  try {
    const refreshEndpoint = `${API_CONFIG.API_ROOT_URL}/auth/refresh`;

    const refreshToken = localStorage.getItem("refresh_token");
    const body = refreshToken ? { refreshToken } : undefined;

    const response = await fetch(refreshEndpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: body ? JSON.stringify(body) : undefined,
    });

    if (!response.ok) {
      throw new Error(`Failed to refresh token: ${response.status}`);
    }

    const responseData = await response.json();
    const tokens = responseData.tokens;

    if (tokens?.accessToken && tokens?.refreshToken) {
      localStorage.setItem("access_token", tokens.accessToken);
      localStorage.setItem("refresh_token", tokens.refreshToken);
    }

    return true;
  } catch {
    return false;
  }
}

function buildApiUrl(path: string, baseOverride?: string): string {
  if (path.startsWith("http://") || path.startsWith("https://")) {
    return path;
  }
  if (baseOverride) {
    const cleanPath = path.startsWith("/") ? path.substring(1) : path;
    const base = baseOverride.endsWith("/") ? baseOverride : `${baseOverride}/`;
    return `${base}${cleanPath}`;
  }
  if (path.startsWith(API_CONFIG.API_URL)) {
    return path;
  }
  const cleanPath = path.startsWith("/") ? path.substring(1) : path;
  const baseUrl = API_CONFIG.API_URL.endsWith("/")
    ? API_CONFIG.API_URL
    : `${API_CONFIG.API_URL}/`;
  return `${baseUrl}${cleanPath}`;
}

export async function fetchApi<T = unknown>(
  path: string,
  options: FetchApiOptions = {},
): Promise<T> {
  const {
    method = "GET",
    headers = {},
    body,
    parseJson = true,
    _retry = false,
    baseUrl,
    ...restOptions
  } = options;

  let authHeader = {};
  const accessToken = localStorage.getItem("access_token");
  if (accessToken) {
    authHeader = { Authorization: `Bearer ${accessToken}` };
  }

  const isFormData = body instanceof FormData;

  // Organização ativa: injeta o header para o backend operar no contexto dela.
  // Desde o AB#84509 vale para qualquer usuário, não só superadmin — quem manda no
  // que é aceito é o backend, que só honra a organização se o requester for
  // superadmin ou tiver vínculo nela. Continuar gateando pela presença da chave
  // evita depender do claim `role` do JWT (o token Supabase carrega
  // role='authenticated', não a role do app, e a detecção via token falharia).
  let superAdminOrgHeader = {};
  const storedSelectedOrg =
    localStorage.getItem("prodgy_active_organization") ??
    localStorage.getItem("prodgy_superadmin_selected_org");
  if (storedSelectedOrg) {
    try {
      const parsed = JSON.parse(storedSelectedOrg);
      if (parsed?.id) {
        superAdminOrgHeader = { "X-Organization-Id": parsed.id };
      }
    } catch {
      /* ignore */
    }
  }

  const defaultHeaders: HeadersInit = {
    ...(!isFormData && { "Content-Type": "application/json" }),
    ...authHeader,
    ...superAdminOrgHeader,
    ...headers,
  };

  const fetchOptions: RequestInit = {
    method,
    headers: defaultHeaders,
    ...restOptions,
  };

  if (body && method !== "GET") {
    fetchOptions.body = body instanceof FormData ? body : JSON.stringify(body);
  }

  const url = buildApiUrl(path, baseUrl);

  const executeRequest = async (): Promise<T> => {
    const response = await fetch(url, fetchOptions);

    // Handle authentication errors (401)
    const refreshPath = "/auth/refresh";
    if (response.status === 401 && !_retry && !path.endsWith(refreshPath)) {
      if (isRefreshing) {
        return new Promise<T>((resolve, reject) => {
          refreshQueue.push({
            resolve: resolve as (value: unknown) => void,
            reject,
            request: () => fetchApi<T>(path, { ...options, _retry: true }),
          });
        });
      }

      isRefreshing = true;

      try {
        const refreshed = await Promise.race([
          refreshAuthToken(),
          new Promise<boolean>((_, reject) =>
            setTimeout(
              () => reject(new Error("Refresh timeout after 10s")),
              10_000,
            ),
          ),
        ]);

        if (refreshed) {
          processQueue();
          return fetchApi<T>(path, { ...options, _retry: true });
        } else {
          if (!isPublicPage()) {
            window.location.href = "/login";
          }
          throw new Error("Falha na autenticação");
        }
      } catch (refreshError) {
        processQueue(refreshError);
        if (!isPublicPage()) {
          window.location.href = "/login";
        }
        throw refreshError;
      } finally {
        isRefreshing = false;
      }
    }

    // Handle rate limit (v2 throttle: 10/min login, 30/min refresh, 5/15min reset)
    if (response.status === 429) {
      const retryAfterHeader = response.headers.get("Retry-After");
      const seconds = retryAfterHeader ? parseInt(retryAfterHeader, 10) : null;
      toast({
        title: "Muitas tentativas",
        description: seconds
          ? `Aguarde ${seconds} segundo${seconds !== 1 ? "s" : ""} antes de tentar novamente.`
          : "Aguarde antes de tentar novamente.",
        variant: "destructive",
      });
      const rateLimitError = new Error("Too many requests") as ApiError;
      rateLimitError.status = 429;
      rateLimitError.code = "E_RATE_LIMITED";
      if (seconds !== null) rateLimitError.retryAfter = seconds;
      rateLimitError.data = { code: "E_RATE_LIMITED", retryAfter: seconds };
      rateLimitError.response = { status: 429, data: rateLimitError.data };
      throw rateLimitError;
    }

    // Handle other non-successful responses
    if (!response.ok) {
      let errorData = null;
      try {
        const responseText = await response.text();
        errorData = responseText ? JSON.parse(responseText) : null;
      } catch {
        errorData = null;
      }

      const error = new Error(
        errorData?.message ||
          errorData?.error ||
          `Request failed with status ${response.status}`,
      );
      const apiError = error as ApiError;
      apiError.status = response.status;
      apiError.code = errorData?.code;
      apiError.data = errorData;
      apiError.response = { status: response.status, data: errorData };
      throw apiError;
    }

    if (parseJson) {
      if (
        response.status === 204 ||
        response.headers.get("content-length") === "0"
      ) {
        return undefined as T;
      }
      return (await response.json()) as T;
    }

    return response as unknown as T;
  };

  return executeRequest();
}

export function getApi<T = unknown>(
  url: string,
  options: Omit<FetchApiOptions, "method" | "body"> = {},
): Promise<T> {
  return fetchApi<T>(url, { ...options, method: "GET" });
}

export function postApi<T = unknown>(
  url: string,
  body?: unknown,
  options: Omit<FetchApiOptions, "method" | "body"> = {},
): Promise<T> {
  return fetchApi<T>(url, { ...options, method: "POST", body });
}

export function putApi<T = unknown>(
  url: string,
  body?: unknown,
  options: Omit<FetchApiOptions, "method" | "body"> = {},
): Promise<T> {
  return fetchApi<T>(url, { ...options, method: "PUT", body });
}

export function delApi<T = unknown>(
  url: string,
  options: Omit<FetchApiOptions, "method"> = {},
): Promise<T> {
  return fetchApi<T>(url, { ...options, method: "DELETE" });
}

// ============================================================================
// v2 helpers (AdonisJS api-v2)
// Hit API_ROOT_URL directly, no /api/ prefix, no envelope unwrap (v2 returns raw).
// ============================================================================

export function get<T = unknown>(
  path: string,
  options: Omit<FetchApiOptions, "method" | "body" | "baseUrl"> = {},
): Promise<T> {
  return fetchApi<T>(path, {
    ...options,
    method: "GET",
    baseUrl: API_CONFIG.API_ROOT_URL,
  });
}

export function post<T = unknown>(
  path: string,
  body?: unknown,
  options: Omit<FetchApiOptions, "method" | "body" | "baseUrl"> = {},
): Promise<T> {
  return fetchApi<T>(path, {
    ...options,
    method: "POST",
    body,
    baseUrl: API_CONFIG.API_ROOT_URL,
  });
}

export function put<T = unknown>(
  path: string,
  body?: unknown,
  options: Omit<FetchApiOptions, "method" | "body" | "baseUrl"> = {},
): Promise<T> {
  return fetchApi<T>(path, {
    ...options,
    method: "PUT",
    body,
    baseUrl: API_CONFIG.API_ROOT_URL,
  });
}

export function patch<T = unknown>(
  path: string,
  body?: unknown,
  options: Omit<FetchApiOptions, "method" | "body" | "baseUrl"> = {},
): Promise<T> {
  return fetchApi<T>(path, {
    ...options,
    method: "PATCH",
    body,
    baseUrl: API_CONFIG.API_ROOT_URL,
  });
}

export function del<T = unknown>(
  path: string,
  options: Omit<FetchApiOptions, "method" | "baseUrl"> = {},
): Promise<T> {
  return fetchApi<T>(path, {
    ...options,
    method: "DELETE",
    baseUrl: API_CONFIG.API_ROOT_URL,
  });
}

// ============================================================================
// Engine execution base.
// A superficie HTTP do engine (/api/workflow/*, /api/nodes/*) mantem o prefixo
// `/api`, e a base da api (`API_ROOT_URL`) aponta para a raiz — daí o append.
// ============================================================================

export function engineApiBase(): string {
  return `${API_CONFIG.API_ROOT_URL}/api`;
}

/**
 * SSE events URL for a workflow execution. EventSource can't send the
 * Authorization header, so the JWT goes in `?access_token=` (silentAuth reads
 * it from the query).
 */
export function engineEventsUrl(executionId: string): string {
  const token = localStorage.getItem("access_token") ?? "";
  return `${API_CONFIG.API_ROOT_URL}/api/workflow/${executionId}/events?access_token=${encodeURIComponent(token)}`;
}
